import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react'
import { User as SupabaseUser } from '@supabase/supabase-js'
import { supabase, SecretUser, SecretAdminUser, TABLES, getSecretUser } from '../lib/supabase'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../components/ui/EnterpriseToast'

interface AuthContextType {
  user: SupabaseUser | null
  secretUser: SecretUser | null
  adminUser: SecretAdminUser | null
  loading: boolean
  isAdmin: boolean
  isAuthenticated: boolean
  signIn: (email: string, password: string) => Promise<void>
  signInWithPhone: (phone: string, password: string) => Promise<void>
  signUp: (email: string, password: string, userData?: Partial<SecretUser>) => Promise<void>
  signUpWithPhone: (phone: string, password: string, userData?: Partial<SecretUser>) => Promise<void>
  signOut: () => Promise<void>
  sendVerificationCode: (contact: string, type: 'email' | 'sms') => Promise<void>
  verifyCode: (contact: string, code: string, type: 'email' | 'sms') => Promise<void>
  resetPassword: (identifier: string, type?: 'email' | 'phone') => Promise<void>
  updateProfile: (updates: Partial<SecretUser>) => Promise<void>
  refreshUser: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<SupabaseUser | null>(null)
  const [secretUser, setSecretUser] = useState<SecretUser | null>(null)
  const [adminUser, setAdminUser] = useState<SecretAdminUser | null>(null)
  const [loading, setLoading] = useState(true)

  // Use ref instead of state so we have synchronous access without re-renders
  const isSigningOutRef = useRef(false)
  const signOutTimestampRef = useRef<number>(0)
  const isSigningInRef = useRef(false)

  // Fetch SECRET user profile
  const fetchSecretUser = async (userId: string) => {
    try {
      console.log('[AuthContext] fetchSecretUser started for userId:', userId)
      const userData = await getSecretUser(userId)
      console.log('[AuthContext] fetchSecretUser got user data:', userData?.id)

      // Only update if data actually changed to prevent unnecessary re-renders
      setSecretUser(prev => {
        console.log('[AuthContext] setSecretUser - prev:', prev?.id, 'new:', userData?.id)
        if (prev?.id === userData?.id && JSON.stringify(prev) === JSON.stringify(userData)) {
          console.log('[AuthContext] setSecretUser - returning prev (no change)')
          return prev
        }
        console.log('[AuthContext] setSecretUser - setting new userData')
        return userData
      })

      // Check if user is admin
      if (userData) {
        const { data: adminData, error: adminError } = await supabase
          .from(TABLES.ADMIN_USERS)
          .select('*')
          .eq('user_id', userId)
          .maybeSingle()

        if (adminError) {
          console.error('[AuthContext] Error fetching admin data:', adminError)
        }

        console.log('[AuthContext] Admin check complete. Is admin:', !!adminData)
        setAdminUser(prev => {
          if (JSON.stringify(prev) === JSON.stringify(adminData)) {
            return prev
          }
          return adminData
        })
      } else {
        // Clear admin state if no user data
        setAdminUser(null)
      }
      console.log('[AuthContext] fetchSecretUser completed')
    } catch (error) {
      console.error('[AuthContext] Error fetching SECRET user:', error)
      // Don't clear state on error - keep existing state to prevent white screen
      // Only log the error
    }
  }

  // Initialize auth state
  useEffect(() => {
    let mounted = true

    // Timeout protection: if auth takes more than 10s, force loading to false
    const loadingTimeout = setTimeout(() => {
      if (mounted) {
        console.error('[AuthContext] Auth initialization timeout - forcing loading to false')
        setLoading(false)
      }
    }, 10000)

    const initializeAuth = async () => {
      try {
        // Get current session
        const { data: { session } } = await supabase.auth.getSession()

        if (mounted) {
          if (session?.user) {
            setUser(session.user)
            await fetchSecretUser(session.user.id)
          }
          clearTimeout(loadingTimeout)
          setLoading(false)
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
        if (mounted) {
          clearTimeout(loadingTimeout)
          setLoading(false)
        }
      }
    }

    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return

        const timeSinceSignOut = Date.now() - signOutTimestampRef.current
        console.log('[AuthContext] Auth state change event:', event, 'has session:', !!session, 'isSigningOut:', isSigningOutRef.current, 'isSigningIn:', isSigningInRef.current, 'timeSinceSignOut:', timeSinceSignOut)

        // Ignore SIGNED_IN events during sign out UNLESS there's an active sign-in attempt
        // This prevents immediate re-authentication during sign out while allowing legitimate sign-ins
        if (isSigningOutRef.current && event === 'SIGNED_IN' && !isSigningInRef.current) {
          console.log('[AuthContext] ✅ Ignoring SIGNED_IN event during sign out (no active sign-in attempt)')
          return
        }

        // Never show loading for SIGNED_IN events - they happen on tab switch and page refresh
        // The page is already authenticated, just needs to sync state silently
        // Only show loading for actual sign outs
        const shouldShowLoading = (
          event === 'SIGNED_OUT'
        )

        console.log('[AuthContext] Should show loading:', shouldShowLoading)

        if (shouldShowLoading) {
          setLoading(true)
        }

        if (session?.user) {
          // Only update user if it actually changed to prevent unnecessary re-renders
          setUser(prev => {
            if (prev?.id === session.user.id) return prev
            return session.user
          })

          // Always fetch user data on SIGNED_IN to ensure fresh data after sign in/out cycles
          // Skip only for TOKEN_REFRESHED which happens frequently
          const shouldFetchUser = event !== 'TOKEN_REFRESHED'

          if (shouldFetchUser) {
            console.log('[AuthContext] Fetching user data for event:', event)
            // Fire and forget - don't await to prevent blocking the auth state machine
            fetchSecretUser(session.user.id).catch(error => {
              console.error('[AuthContext] Unhandled error in fetchSecretUser:', error)
            })
          } else {
            console.log('[AuthContext] Skipping user fetch for event:', event)
          }
        } else {
          console.log('[AuthContext] No session, clearing state')
          setUser(null)
          setSecretUser(null)
          setAdminUser(null)
        }

        if (shouldShowLoading) {
          setLoading(false)
        }
      }
    )

    return () => {
      mounted = false
      clearTimeout(loadingTimeout)
      subscription.unsubscribe()
    }
  }, [])

  // Sign in with email
  const signIn = React.useCallback(async (email: string, password: string) => {
    try {
      // Clear any lingering sign-out state immediately
      isSigningOutRef.current = false
      isSigningInRef.current = true
      console.log('[AuthContext] signIn - clearing isSigningOut and setting isSigningInRef to true')

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) throw error

      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Welcome Back"
          message="You've been signed in successfully"
        />
      ))
    } catch (error: any) {
      console.error('Sign in error:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Sign In Failed"
          message={error.message || "Failed to sign in"}
        />
      ))
      isSigningInRef.current = false
      throw error
    } finally {
      // Clear the flag after a delay to allow auth state to propagate
      setTimeout(() => {
        isSigningInRef.current = false
        console.log('[AuthContext] signIn - clearing isSigningInRef')
      }, 1000)
    }
  }, [])

  // Sign in with phone (requires phone auth setup in Supabase)
  const signInWithPhone = React.useCallback(async (phone: string, password: string) => {
    try {
      // Clear any lingering sign-out state immediately
      isSigningOutRef.current = false
      isSigningInRef.current = true
      console.log('[AuthContext] signInWithPhone - clearing isSigningOut and setting isSigningInRef to true')

      const { error } = await supabase.auth.signInWithPassword({
        phone,
        password
      })

      if (error) throw error

      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Welcome Back"
          message="You've been signed in successfully"
        />
      ))
    } catch (error: any) {
      console.error('Phone sign in error:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Sign In Failed"
          message={error.message || "Failed to sign in"}
        />
      ))
      isSigningInRef.current = false
      throw error
    } finally {
      // Clear the flag after a delay to allow auth state to propagate
      setTimeout(() => {
        isSigningInRef.current = false
        console.log('[AuthContext] signInWithPhone - clearing isSigningInRef')
      }, 1000)
    }
  }, [])

  // Sign up with email
  const signUp = React.useCallback(async (email: string, password: string, userData?: Partial<SecretUser>) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData ? {
            first_name: userData.first_name,
            last_name: userData.last_name,
            full_name: userData.first_name && userData.last_name 
              ? `${userData.first_name} ${userData.last_name}`.trim()
              : userData.first_name || userData.last_name || '',
            zip_code: userData.zip_code
          } : undefined
        }
      })

      if (error) throw error

      if (data.user) {
        // Wait a moment for auth session to propagate and trigger to run
        await new Promise(resolve => setTimeout(resolve, 100))

        // Poll for trigger-created profile instead of inserting client-side
        const maxAttempts = 6 // ~1.5 seconds total
        let attempt = 0
        let profile: SecretUser | null = null
        while (attempt < maxAttempts) {
          try {
            profile = await getSecretUser(data.user.id)
            if (profile) break
          } catch (e) {
            // Ignore transient errors like 401/406 during propagation
          }
          await new Promise(r => setTimeout(r, 250))
          attempt++
        }

        if (!profile) {
          console.warn('Profile not yet available after signup; will rely on auth state change to fetch later.')
        }
      }

      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Account Created"
          message="Please check your email for verification"
        />
      ))
    } catch (error: any) {
      console.error('Sign up error:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Account Creation Failed"
          message={error.message || "Failed to create account"}
        />
      ))
      throw error
    }
  }, [])

  // Sign up with phone
  const signUpWithPhone = React.useCallback(async (phone: string, password: string, userData?: Partial<SecretUser>) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        phone,
        password,
        options: {
          data: userData ? {
            first_name: userData.first_name,
            last_name: userData.last_name,
            full_name: userData.first_name && userData.last_name
              ? `${userData.first_name} ${userData.last_name}`.trim()
              : userData.first_name || userData.last_name || '',
            zip_code: userData.zip_code
          } : undefined
        }
      })

      if (error) throw error

      if (data.user) {
        // Wait a moment for auth session to propagate and trigger to run
        await new Promise(resolve => setTimeout(resolve, 100))

        // Poll for trigger-created profile instead of inserting client-side
        const maxAttempts = 6 // ~1.5 seconds total
        let attempt = 0
        let profile: SecretUser | null = null
        while (attempt < maxAttempts) {
          try {
            profile = await getSecretUser(data.user.id)
            if (profile) break
          } catch (e) {
            // Ignore transient errors like 401/406 during propagation
          }
          await new Promise(r => setTimeout(r, 250))
          attempt++
        }

        if (!profile) {
          console.warn('Profile not yet available after signup; will rely on auth state change to fetch later.')
        }

        // Ensure zip_code and other metadata is saved to secret_users table
        // This is critical for phone signups as the trigger may not copy all metadata
        if (userData && profile) {
          try {
            const { error: updateError } = await supabase
              .from(TABLES.USERS)
              .update({
                first_name: userData.first_name,
                last_name: userData.last_name,
                zip_code: userData.zip_code
              })
              .eq('id', data.user.id)

            if (updateError) {
              console.error('Error updating user profile with metadata:', updateError)
            } else {
              console.log('[signUpWithPhone] Successfully updated user metadata including zip_code')
            }
          } catch (err) {
            console.error('Failed to update user metadata in secret_users:', err)
          }
        }
      }

      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Account Created"
          message="Please verify your phone number"
        />
      ))
    } catch (error: any) {
      console.error('Phone sign up error:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Account Creation Failed"
          message={error.message || "Failed to create account"}
        />
      ))
      throw error
    }
  }, [])

  // Sign out
  const signOut = React.useCallback(async () => {
    console.log('[AuthContext] signOut function called')

    // Clear local state immediately to update UI
    console.log('[AuthContext] Clearing local state immediately...')
    setUser(null)
    setSecretUser(null)
    setAdminUser(null)

    // Clear all localStorage keys related to Supabase auth FIRST
    console.log('[AuthContext] Clearing localStorage...')
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') || key.includes('supabase')) {
        localStorage.removeItem(key)
        console.log('[AuthContext] Cleared localStorage key:', key)
      }
    })

    // Try to sign out properly with a very short timeout
    // If it succeeds quickly, great. If not, we've already cleared everything.
    const signOutPromise = supabase.auth.signOut()
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Sign out timeout')), 500)
    )

    try {
      await Promise.race([signOutPromise, timeoutPromise])
      console.log('[AuthContext] supabase.auth.signOut() completed successfully')
    } catch (error) {
      console.warn('[AuthContext] Sign out timed out or failed, but localStorage already cleared')
    }

    // Always reload the page to ensure clean state
    // This is the most reliable way to clear stale Supabase client state
    console.log('[AuthContext] Reloading page to ensure clean auth state')
    window.location.href = '/'
  }, [])

  // Send verification code
  const sendVerificationCode = React.useCallback(async (contact: string, type: 'email' | 'sms') => {
    try {
      // This would typically call a custom function or third-party service
      // For now, we'll use Supabase's built-in email verification
      if (type === 'email') {
        
        // Get user metadata for email personalization
        const userData = secretUser ? {
          full_name: secretUser.first_name && secretUser.last_name 
            ? `${secretUser.first_name} ${secretUser.last_name}`.trim()
            : secretUser.first_name || '',
          first_name: secretUser.first_name || '',
          last_name: secretUser.last_name || '',
          // Also add camelCase version in case Supabase expects it
          fullName: secretUser.first_name && secretUser.last_name 
            ? `${secretUser.first_name} ${secretUser.last_name}`.trim()
            : secretUser.first_name || ''
        } : undefined

        const { error } = await supabase.auth.resend({
          type: 'signup',
          email: contact.trim().toLowerCase(),
          options: userData ? { data: userData } : undefined
        })
        if (error) throw error
      }

      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Verification Code Sent"
          message={`Check your ${type === 'email' ? 'email' : 'phone'} for the code`}
        />
      ))
    } catch (error: any) {
      console.error('Verification code error:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Sending Failed"
          message="Could not send verification code. Please try again"
        />
      ))
      throw error
    }
  }, [secretUser])

  // Verify code
  const verifyCode = React.useCallback(async (contact: string, code: string, type: 'email' | 'sms') => {
    try {
      if (type === 'email') {
        const { error } = await supabase.auth.verifyOtp({
          email: contact.trim().toLowerCase(),
          token: code,
          type: 'email'
        })
        if (error) throw error
      } else {
        const { error } = await supabase.auth.verifyOtp({
          phone: contact,
          token: code,
          type: 'sms'
        })
        if (error) throw error
      }

      // Verification status is now automatically synced via database triggers
      // (sync_email_verification_status and sync_phone_verification_status)
      // Wait a moment for the trigger to propagate, then refresh user data
      await new Promise(resolve => setTimeout(resolve, 500))

      // Refresh user data to get updated verification status
      if (user) {
        await fetchSecretUser(user.id)
      }

      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Verified"
          message="Your account has been verified"
        />
      ))
    } catch (error: any) {
      console.error('Verification error:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Verification Failed"
          message="The code you entered is invalid or expired"
        />
      ))
      throw error
    }
  }, [user])

  // Reset password
  const resetPassword = React.useCallback(async (identifier: string, type: 'email' | 'phone' = 'email') => {
    try {
      if (type === 'phone') {
        // For phone reset, send OTP via SMS
        const { error } = await supabase.auth.signInWithOtp({
          phone: identifier,
          options: {
            channel: 'sms'
          }
        })
        if (error) throw error
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="success"
            title="Reset Code Sent"
            message="Check your phone for the password reset code"
          />
        ))
      } else {
        // For email reset, use password reset flow
        const { error } = await supabase.auth.resetPasswordForEmail(identifier, {
          redirectTo: 'https://secret.tailtations.com/reset-password',
        })
        if (error) throw error
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="success"
            title="Reset Email Sent"
            message="Check your inbox for password reset instructions"
          />
        ))
      }
    } catch (error: any) {
      console.error('Password reset error:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Reset Failed"
          message={`Could not send password reset ${type === 'email' ? 'email' : 'code'}`}
        />
      ))
      throw error
    }
  }, [])

  // Update profile
  const updateProfile = React.useCallback(async (updates: Partial<SecretUser>) => {
    try {
      if (!user) throw new Error('No user logged in')

      const { data, error } = await supabase
        .from(TABLES.USERS)
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)
        .select()
        .single()

      if (error) throw error

      setSecretUser(data)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Profile Updated"
          message="Your changes have been saved"
        />
      ))
    } catch (error: any) {
      console.error('Profile update error:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Update Failed"
          message="Could not save profile changes. Please try again"
        />
      ))
      throw error
    }
  }, [user])

  // Refresh user data
  const refreshUser = React.useCallback(async () => {
    if (user) {
      console.log('[AuthContext] refreshUser called for user:', user.id)
      await fetchSecretUser(user.id)
      console.log('[AuthContext] refreshUser completed')
    } else {
      console.log('[AuthContext] refreshUser called but no user present')
    }
  }, [user])

  const isAuthenticated = !!user && !loading && (secretUser?.email_verified === true || secretUser?.phone_verified === true)

  // Log authentication state changes for debugging
  useEffect(() => {
    console.log('[AuthContext] Authentication state:', {
      hasUser: !!user,
      loading,
      hasSecretUser: !!secretUser,
      email_verified: secretUser?.email_verified,
      phone_verified: secretUser?.phone_verified,
      isAuthenticated
    })
  }, [user, loading, secretUser, isAuthenticated])

  const value: AuthContextType = React.useMemo(() => ({
    user,
    secretUser,
    adminUser,
    loading,
    isAdmin: !!adminUser,
    isAuthenticated,
    signIn,
    signInWithPhone,
    signUp,
    signUpWithPhone,
    signOut,
    sendVerificationCode,
    verifyCode,
    resetPassword,
    updateProfile,
    refreshUser
  }), [
    user,
    secretUser,
    adminUser,
    loading,
    isAuthenticated,
    signIn,
    signInWithPhone,
    signUp,
    signUpWithPhone,
    signOut,
    sendVerificationCode,
    verifyCode,
    resetPassword,
    updateProfile,
    refreshUser
  ])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthProvider